import { z } from "zod";
import { CUSTOM_PREFIX, categoryFor, fieldFor, fieldKind, fieldLabel, typeFor } from "./catalog";
import { addDays, addMonths, diffDays, formatDate, isValidIsoDate } from "./dates";
import { currencyDigits, isKnownCurrency } from "./money";

export const RENEWAL_TYPES = ["none", "auto", "manual"] as const;
export type RenewalType = (typeof RENEWAL_TYPES)[number];

export const STATUSES = ["active", "terminated"] as const;
export type ContractStatus = (typeof STATUSES)[number];

export const OUTCOMES = ["renewed", "renegotiated", "terminated"] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** Reminders go out this many days before each notice deadline (F5). */
export const REMINDER_OFFSETS = [90, 30, 7] as const;

export const LIMITS = {
  title: 200,
  counterparty: 200,
  notes: 20_000,
  tags: 20,
  tagLength: 32,
  noticeDays: 3650,
  termMonths: 120,
  valueMinor: 1e15,
  parties: 50,
  partyName: 200,
  partyRole: 60,
  details: 300,
  detailKey: 64,
  detailText: 500,
  detailLongText: 5000,
} as const;

/** A person or company involved in the contract, e.g. the lessee, a guarantor or a witness. */
export interface Party {
  name: string;
  /** "Lessee (tenant)", "Guarantor"…; free text, suggested by the contract type. */
  role: string;
  email: string | null;
  phone: string | null;
}

/** Extra terms: catalogue key (or "x:<label>" for a custom one) → value as text. Dates are YYYY-MM-DD, yes/no is "yes" or "no". */
export type Details = Record<string, string>;

export interface DocumentMeta {
  id: string;
  contractId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: "pending" | "ready";
  textMethod: string | null;
  createdAt: string;
}

export interface ContractFields {
  title: string;
  counterparty: string | null;
  valueMinor: number | null;
  currency: string | null;
  startDate: string | null;
  endDate: string | null;
  renewalType: RenewalType;
  renewalTermMonths: number | null;
  noticePeriodDays: number | null;
  ownerUserId: string | null;
  tags: string[];
  notes: string | null;
  status: ContractStatus;
  /** Catalogue type id (shared/catalog.ts), e.g. "rent_agreement". */
  type: string | null;
  parties: Party[];
  details: Details;
}

export interface Contract extends ContractFields {
  id: string;
  nextNoticeDeadline: string | null;
  version: number;
  seq: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  documents: DocumentMeta[];
}

/** Tags are trimmed, lower-cased, de-duplicated; "#Vendor, vendor" becomes ["vendor"]. */
export function normalizeTags(tags: readonly unknown[]): string[] {
  const seen = new Set<string>();
  for (const t of tags) {
    if (typeof t !== "string") continue;
    const tag = t.trim().replace(/^#+/, "").trim().replace(/\s+/g, " ").toLowerCase();
    if (tag) seen.add(tag);
  }
  return [...seen];
}

/** Turns "vendor, SaaS ,, #legal" into ["vendor", "saas", "legal"]. */
export function parseTagInput(input: string): string[] {
  return normalizeTags(input.split(/[,;\n]/));
}

const optionalText = (max: number, label: string) =>
  z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (typeof v === "string" ? v.replaceAll("\0", "").trim() : v))
    .transform((v) => (v === "" || v === undefined ? null : v))
    .refine((v) => v === null || v.length <= max, `${label} must be ${max} characters or fewer.`);

const isoDate = (label: string) =>
  z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v === "" || v === undefined ? null : v))
    .refine((v) => v === null || isValidIsoDate(v), `${label} must be a real calendar date (YYYY-MM-DD).`);

const optionalInt = (min: number, max: number, message: string) =>
  z
    .union([z.number(), z.null()])
    .optional()
    .transform((v) => (v === undefined ? null : v))
    .refine((v) => v === null || (Number.isInteger(v) && v >= min && v <= max), message);

const partySchema = z.object({
  name: z
    .string({ error: "Each person needs a name." })
    .transform((v) => v.replaceAll("\0", "").trim().replace(/\s+/g, " "))
    .refine((v) => v.length <= LIMITS.partyName, `Names must be ${LIMITS.partyName} characters or fewer.`),
  role: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (v ?? "").replaceAll("\0", "").trim().replace(/\s+/g, " "))
    .refine((v) => v.length <= LIMITS.partyRole, `Roles must be ${LIMITS.partyRole} characters or fewer.`),
  email: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (typeof v === "string" ? v.trim().toLowerCase() || null : null))
    .refine((v) => v === null || (v.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v)), "That email address doesn't look right."),
  phone: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (typeof v === "string" ? v.trim() || null : null))
    .refine((v) => v === null || /^[+\d][\d\s()-]{4,30}$/.test(v), "That phone number doesn't look right."),
});

const DETAIL_KEY = /^[a-z0-9_]{1,64}$/;

/** Why a term's value is invalid, or null. Unknown catalogue keys (from a newer app) are kept as text. */
export function detailProblem(key: string, value: string): string | null {
  const label = fieldLabel(key);
  if (key.startsWith(CUSTOM_PREFIX)) {
    const name = key.slice(CUSTOM_PREFIX.length);
    if (!name.trim() || name.length > LIMITS.detailKey) return `A custom term's name must be 1 to ${LIMITS.detailKey} characters.`;
  } else if (!DETAIL_KEY.test(key)) return `"${key}" isn't a valid term.`;
  const field = fieldFor(key);
  const max = field?.kind === "longtext" ? LIMITS.detailLongText : LIMITS.detailText;
  if (value.length > max) return `${label} must be ${max} characters or fewer.`;
  switch (field?.kind) {
    case "date":
      return isValidIsoDate(value) ? null : `${label} must be a real date (YYYY-MM-DD).`;
    case "number":
    case "money":
      return /^\d+(\.\d+)?$/.test(value) && Number(value) <= LIMITS.valueMinor ? null : `${label} must be a number.`;
    case "percent":
      return /^\d+(\.\d+)?$/.test(value) && Number(value) <= 1000 ? null : `${label} must be a percentage, like 7.5.`;
    case "yesno":
      return value === "yes" || value === "no" ? null : `${label} must be yes or no.`;
    case "select":
      return field.options!.includes(value) ? null : `${label} must be one of: ${field.options!.join(", ")}.`;
    default:
      return null;
  }
}

/** Trims values, drops empty ones and normalises numbers ("1,20,000" → "120000") and yes/no. */
export function normalizeDetailValue(key: string, raw: unknown): string {
  let v = typeof raw === "boolean" ? (raw ? "yes" : "no") : typeof raw === "number" ? String(raw) : typeof raw === "string" ? raw : "";
  v = v.replaceAll("\0", "").trim();
  const kind = fieldFor(key)?.kind;
  if (kind === "number" || kind === "money" || kind === "percent") v = v.replace(/[,\s%]/g, "");
  if (kind === "yesno") v = /^(y|yes|true)$/i.test(v) ? "yes" : /^(n|no|false)$/i.test(v) ? "no" : v;
  if (kind === "select") v = fieldFor(key)!.options!.find((o) => o.toLowerCase() === v.toLowerCase()) ?? v;
  return v;
}

/** Field rules shared by create and update. Unknown keys are dropped. */
export const contractFieldsSchema = z.object({
  title: z
    .string({ error: "Give the contract a name." })
    .transform((v) => v.replaceAll("\0", "").trim())
    .refine((v) => v.length > 0, "Give the contract a name.")
    .refine((v) => v.length <= LIMITS.title, `The name must be ${LIMITS.title} characters or fewer.`),
  counterparty: optionalText(LIMITS.counterparty, "Counterparty"),
  valueMinor: optionalInt(0, LIMITS.valueMinor, "Value must be a whole number of cents between 0 and 10 trillion."),
  currency: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => (typeof v === "string" ? v.trim().toUpperCase() || null : null))
    .refine((v) => v === null || isKnownCurrency(v), "Choose a currency from the list, for example USD or INR."),
  startDate: isoDate("Start date"),
  endDate: isoDate("End date"),
  renewalType: z.enum(RENEWAL_TYPES, { error: "Renewal must be none, auto or manual." }).optional().default("none"),
  renewalTermMonths: optionalInt(1, LIMITS.termMonths, `Renewal term must be between 1 and ${LIMITS.termMonths} months.`),
  noticePeriodDays: optionalInt(0, LIMITS.noticeDays, `Notice period must be between 0 and ${LIMITS.noticeDays} days.`),
  ownerUserId: z
    .union([z.uuid({ error: "Owner is not a valid user." }), z.null()])
    .optional()
    .transform((v) => v ?? null),
  tags: z
    .array(z.string({ error: "Tags must be text." }), { error: "Tags must be a list." })
    .optional()
    .default([])
    .transform(normalizeTags)
    .refine((t) => t.length <= LIMITS.tags, `Use at most ${LIMITS.tags} tags.`)
    .refine((t) => t.every((x) => x.length <= LIMITS.tagLength), `Each tag must be ${LIMITS.tagLength} characters or fewer.`),
  notes: optionalText(LIMITS.notes, "Notes"),
  status: z.enum(STATUSES).optional().default("active"),
  type: z
    .union([z.string(), z.null()])
    .optional()
    .transform((v) => v || null)
    .refine((v) => v === null || !!typeFor(v), "Choose a contract type from the list."),
  parties: z
    .array(partySchema, { error: "People must be a list." })
    .optional()
    .default([])
    .transform((list) => list.filter((p) => p.name || p.email || p.phone))
    .refine((list) => list.length <= LIMITS.parties, `Add at most ${LIMITS.parties} people.`)
    .refine((list) => list.every((p) => p.name), "Each person needs a name."),
  details: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]), { error: "Terms must be a set of values." })
    .optional()
    .default({})
    .transform((rec) => {
      const out: Details = {};
      for (const [k, raw] of Object.entries(rec)) {
        const v = normalizeDetailValue(k, raw);
        if (v) out[k] = v;
      }
      return out;
    })
    .superRefine((rec, ctx) => {
      if (Object.keys(rec).length > LIMITS.details) ctx.addIssue({ code: "custom", message: `Add at most ${LIMITS.details} terms.` });
      for (const [k, v] of Object.entries(rec)) {
        const problem = detailProblem(k, v);
        if (problem) ctx.addIssue({ code: "custom", message: problem });
      }
    }),
});

/** Rules that need more than one field. Applied to the full contract after a create or a merged update. */
export function crossFieldIssues(c: ContractFields): { path: string; message: string }[] {
  const issues: { path: string; message: string }[] = [];
  if (c.startDate && c.endDate && c.endDate < c.startDate) {
    issues.push({ path: "endDate", message: "The end date is before the start date." });
  }
  if (c.valueMinor !== null && !c.currency) {
    issues.push({ path: "currency", message: "Choose the currency for the value." });
  } else if (!c.currency && Object.keys(c.details ?? {}).some((k) => fieldFor(k)?.kind === "money")) {
    issues.push({ path: "currency", message: "Choose the currency for the amounts." });
  }
  if (c.renewalType === "auto" && c.renewalTermMonths === null) {
    issues.push({ path: "renewalTermMonths", message: "How long does each automatic renewal last? Enter the term in months." });
  }
  if (c.renewalType === "auto" && !c.endDate) {
    issues.push({ path: "endDate", message: "An auto-renewing contract needs the end date of its current term." });
  }
  return issues;
}

/** Normalises values that make no sense together instead of rejecting them. */
export function normalizeFields(c: ContractFields): ContractFields {
  return { ...c, renewalTermMonths: c.renewalType === "none" ? null : c.renewalTermMonths };
}

export const createContractSchema = contractFieldsSchema.extend({
  id: z.uuid({ error: "Contract id must be a UUID." }),
});

export const updateContractSchema = contractFieldsSchema.partial().extend({
  version: z.number({ error: "Missing the version you edited." }).int().min(1),
});

export const outcomeSchema = z.object({
  outcome: z.enum(OUTCOMES, { error: "Outcome must be renewed, renegotiated or terminated." }),
  newEndDate: isoDate("New end date"),
  version: z.number().int().min(1),
});

// ---------------------------------------------------------------------------
// Deadlines

type DeadlineInput = Pick<ContractFields, "endDate" | "renewalType" | "renewalTermMonths" | "noticePeriodDays" | "status">;

const MAX_ROLLS = 2400;

/**
 * End of the term running on `today`. Auto-renewing contracts roll forward by
 * their term after the end date passes; others keep their end date (possibly in the past).
 * Terms are counted from the original end date so month-end dates don't drift.
 */
export function currentTermEnd(c: DeadlineInput, today: string): string | null {
  if (!c.endDate || !isValidIsoDate(c.endDate)) return null;
  if (c.renewalType !== "auto" || !c.renewalTermMonths || c.endDate >= today) return c.endDate;
  const term = c.renewalTermMonths;
  let k = Math.max(1, Math.floor(diffDays(c.endDate, today) / (term * 31)));
  while (k > 1 && addMonths(c.endDate, (k - 1) * term) >= today) k--;
  for (let i = 0; i < MAX_ROLLS; i++, k++) {
    const end = addMonths(c.endDate, k * term);
    if (end >= today) return end;
  }
  return null;
}

export function noticeDeadlineFor(termEnd: string, noticePeriodDays: number | null): string {
  return addDays(termEnd, -(noticePeriodDays ?? 0));
}

/**
 * The deadline the owner must act by next. For an auto-renewing contract whose
 * deadline for the current term has passed, that is the next term's deadline.
 * Null for terminated contracts and contracts without an end date. For other
 * contracts the deadline may be in the past.
 */
export function nextNoticeDeadline(c: DeadlineInput, today: string): string | null {
  if (c.status !== "active") return null;
  const end = currentTermEnd(c, today);
  if (!end) return null;
  let deadline = noticeDeadlineFor(end, c.noticePeriodDays);
  if (deadline >= today || c.renewalType !== "auto" || !c.renewalTermMonths) return deadline;
  // Walk forward term by term; with a notice period longer than one term this takes several steps.
  let termEnd: string | null = end;
  for (let i = 0; i < MAX_ROLLS && termEnd && deadline < today; i++) {
    termEnd = currentTermEnd(c, addDays(termEnd, 1));
    if (termEnd) deadline = noticeDeadlineFor(termEnd, c.noticePeriodDays);
  }
  return deadline;
}

/** Soft warnings shown in the form; they never block saving. */
export function contractWarnings(c: ContractFields, today: string): string[] {
  const warnings: string[] = [];
  if (c.status !== "active") return warnings;
  if (c.endDate && c.startDate && c.noticePeriodDays !== null) {
    const deadline = noticeDeadlineFor(c.endDate, c.noticePeriodDays);
    if (deadline < c.startDate) warnings.push("The notice period is longer than the whole contract, so the notice deadline falls before it starts.");
  }
  if (c.endDate && diffDays(today, c.endDate) > 365 * 50) warnings.push("The end date is more than 50 years away. Is that right?");
  if (c.startDate && diffDays(c.startDate, today) > 365 * 50) warnings.push("The start date is more than 50 years ago. Is that right?");
  if (!c.endDate) warnings.push("Without an end date we can't remind you about this contract.");
  const deadline = nextNoticeDeadline(c, today);
  if (deadline && deadline < today && c.endDate && c.endDate >= today) {
    warnings.push("The notice deadline for this term has already passed.");
  }
  if (c.endDate && c.endDate < today && c.renewalType !== "auto") warnings.push("This contract has already ended.");
  return warnings;
}

/** Applies a reminder outcome (journey step 6). Returns the fields to update or an error. */
export function applyOutcome(
  c: ContractFields,
  outcome: Outcome,
  today: string,
  newEndDate: string | null,
): { ok: true; fields: Partial<ContractFields> } | { ok: false; error: string } {
  if (outcome === "terminated") return { ok: true, fields: { status: "terminated" } };
  if (outcome === "renegotiated") {
    if (!newEndDate) return { ok: true, fields: { status: "active" } };
    if (c.startDate && newEndDate < c.startDate) return { ok: false, error: "The new end date is before the start date." };
    return { ok: true, fields: { status: "active", endDate: newEndDate } };
  }
  // renewed
  if (newEndDate) {
    const current = currentTermEnd(c, today);
    if (current && newEndDate <= current) return { ok: false, error: "The new end date must be after the current end date." };
    return { ok: true, fields: { status: "active", endDate: newEndDate } };
  }
  const current = currentTermEnd(c, today);
  if (!current || !c.renewalTermMonths) {
    return { ok: false, error: "Enter the new end date, since this contract has no renewal term." };
  }
  return { ok: true, fields: { status: "active", endDate: addMonths(current, c.renewalTermMonths) } };
}

export function emptyContractFields(): ContractFields {
  return {
    title: "",
    counterparty: null,
    valueMinor: null,
    currency: null,
    startDate: null,
    endDate: null,
    renewalType: "none",
    renewalTermMonths: null,
    noticePeriodDays: null,
    ownerUserId: null,
    tags: [],
    notes: null,
    status: "active",
    type: null,
    parties: [],
    details: {},
  };
}

export function formatIssues(error: z.ZodError): { path: string; message: string }[] {
  // Reported per top-level field ("tags", not "tags.3") so forms can show them next to the input.
  const seen = new Set<string>();
  return error.issues
    .map((i) => ({ path: String(i.path[0] ?? ""), message: i.message }))
    .filter((i) => (seen.has(i.path) ? false : (seen.add(i.path), true)));
}

/**
 * The type, people and terms as searchable text: "Rent agreement Property & real estate
 * Lessee (tenant) Asha Rao Security deposit 50000…". Indexed for search on the server and
 * matched locally in the browser.
 */
export function extraSearchText(c: Pick<ContractFields, "type" | "parties" | "details">): string {
  const type = typeFor(c.type);
  return [
    type?.label,
    type && categoryFor(type.category)?.label,
    ...(c.parties ?? []).flatMap((p) => [p.role, p.name, p.email, p.phone]),
    ...Object.entries(c.details ?? {}).flatMap(([k, v]) => [fieldLabel(k), v]),
  ]
    .filter(Boolean)
    .join(" ");
}

// --- people and value ---------------------------------------------------------------
// Counterparty and Value used to be fields of their own; they are now a person with a role and
// the "Total contract value" term. Contracts not yet moved over still read sensibly.

/** The contract's total value: the "Total contract value" term, or the old value column. */
export function contractValue(c: Pick<ContractFields, "details" | "valueMinor" | "currency">): { minor: number; currency: string } | null {
  const v = c.details?.contract_value;
  if (v && c.currency) {
    const n = Number(v);
    if (Number.isFinite(n)) return { minor: Math.round(n * 10 ** currencyDigits(c.currency)), currency: c.currency };
  }
  if (c.valueMinor !== null && c.valueMinor !== undefined && c.currency) return { minor: c.valueMinor, currency: c.currency };
  return null;
}

/** "Lessor: R. Shah · Lessee: Asha Mehta"; roles lose their "(landlord)" part. Falls back to the old counterparty. */
export function peopleLine(c: Pick<ContractFields, "parties" | "counterparty">): string {
  const people = (c.parties ?? []).map((p) => (p.role ? `${p.role.replace(/\s*\(.*\)$/, "")}: ${p.name}` : p.name)).join(" · ");
  return people || c.counterparty || "";
}

/** Moves a contract's old counterparty and value into its people and terms, if they aren't there already. */
export function withLegacyMoved<T extends ContractFields>(c: T): T {
  const parties = [...(c.parties ?? [])];
  const details = { ...(c.details ?? {}) };
  if (c.counterparty && !parties.some((p) => p.name.toLowerCase() === c.counterparty!.toLowerCase())) {
    parties.push({ name: c.counterparty, role: "Other party", email: null, phone: null });
  }
  if (c.valueMinor !== null && c.valueMinor !== undefined && c.currency && !details.contract_value) {
    const digits = currencyDigits(c.currency);
    details.contract_value = (c.valueMinor / 10 ** digits).toFixed(digits).replace(/\.0+$/, "");
  }
  return { ...c, counterparty: null, valueMinor: null, parties, details };
}

/** A term's value for reading: dates and money formatted, yes/no capitalised, percentages with %. */
export function formatDetailValue(key: string, value: string, currency: string | null, locale?: string): string {
  switch (fieldKind(key)) {
    case "date":
      return formatDate(value, locale);
    case "money": {
      const n = Number(value);
      if (!Number.isFinite(n)) return value;
      if (!currency) return new Intl.NumberFormat(locale).format(n);
      try {
        return new Intl.NumberFormat(locale, { style: "currency", currency }).format(n);
      } catch {
        return `${n} ${currency}`;
      }
    }
    case "number": {
      const n = Number(value);
      return Number.isFinite(n) ? new Intl.NumberFormat(locale).format(n) : value;
    }
    case "percent":
      return `${value}%`;
    case "yesno":
      return value === "yes" ? "Yes" : value === "no" ? "No" : value;
    default:
      return value;
  }
}
